/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import React from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  mergeRegister,
  $findMatchingParent,
  $getNearestNodeOfType,
} from "@lexical/utils";
import {
  $getSelectionStyleValueForProperty,
  $isParentElementRTL,
  $patchStyleText,
} from "@lexical/selection";
import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $isRootOrShadowRoot,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  createCommand,
  NodeKey,
  SELECTION_CHANGE_COMMAND,
} from "lexical";
import {
  $isListNode,
  INSERT_UNORDERED_LIST_COMMAND,
  insertList,
  ListNode,
} from "@lexical/list";
import { useCallback, useEffect, useRef, useState } from "react";
import { DropdownColorPicker } from "./DropdownColorPicker";
import { $isHeadingNode } from "@lexical/rich-text";
import Alignments from "./Alignments";
import { BulletList } from "./BulletList";
import { NumberList } from "./NumberList";
import Adjust from "./Adjust/Adjust";
import Format from "./Format/Format";
import Bold from "./Bold";
import Italic from "./Italic";
import Underline from "./Underline";
import Edit from "./Edit/Edit";
import Help from "./Help/Help";
import Undo from "./History/Undo";
import Redo from "./History/Redo";
import { ListTypeProps, OptionType, toolTipConstants } from "../LexicalEditor.types";
import { bulletTypeToBlockName, numberTypeList } from "../Helper";
import ClearFormat from "./ClearFormat";
import LinkImage from "./LinkImage";

export const INSERT_IMAGE_COMMAND = createCommand();

const LowPriority = 1;

export const blockTypeToBlockName = {
  h1: "Heading 1",
  h2: "Heading 2",
  h3: "Heading 3",
  h4: "Heading 4",
  h5: "Heading 5",
  h6: "Heading 6",
  paragraph: "Normal",
};

export function dropDownActiveClass(active: boolean) {
  if (active) {
    return "active dropdown-item-active";
  } else {
    return "";
  }
}

export default function ToolbarPlugin({
  options,
  changeStateEditorBody,
  maxLength,
  readOnly,
  messageBody,
}: {
  options: OptionType[];
  changeStateEditorBody: (htmlContent: string) => void;
  maxLength?: number;
  readOnly?: boolean;
  messageBody?: string
}) {
  const [editor] = useLexicalComposerContext();
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [activeEditor, setActiveEditor] = useState(editor);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [fontColor, setFontColor] = useState<string>("#000");
  const [bgColor, setBgColor] = useState<string>("#fff");
  const [isRTL, setIsRTL] = useState(false);
  const [blockType, setBlockType] =
    useState<keyof typeof blockTypeToBlockName>("paragraph");
  const [selectedElementKey, setSelectedElementKey] = useState<NodeKey | null>(
    null
  );
  const [bulletType, setBulletType] = useState(bulletTypeToBlockName["bullet"]);
  const [numberType, setNumberType] = useState(numberTypeList["number"]);
  const [alignment, setAlignment] = useState<string | undefined>(undefined);
  const [listType, setListType] = useState<ListTypeProps | null>(null);
  // const [showSnackbar, setShowSnackbar] = useState(false);

  // useEffect(() => {
  //   return editor.registerUpdateListener(({ editorState }) => {
  //     editorState.read(() => {
  //       const root = $getRoot();
  //       const textContent = root.getTextContent();
  //       const textLength = textContent.length;
  //       if (textLength >= maxLength && !showSnackbar) {
  //         setShowSnackbar(true);
  //       }
  //     });
  //   });
  // }, [editor, maxLength, showSnackbar]);

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      let element =
        anchorNode.getKey() === "root"
          ? anchorNode
          : $findMatchingParent(anchorNode, (e) => {
            const parent = e.getParent();
            return parent !== null && $isRootOrShadowRoot(parent);
          });

      if (element === null) {
        element = anchorNode.getTopLevelElementOrThrow();
      }
      setIsRTL($isParentElementRTL(selection));

      const elementKey = element.getKey();
      const elementDOM = activeEditor.getElementByKey(elementKey);
      if (elementDOM !== null) {
        const alignment = elementDOM.style.textAlign;
        setAlignment(alignment || undefined);

        setSelectedElementKey(elementKey);
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType<ListNode>(anchorNode, ListNode);
          // --- Fix: Use DOM tagName to determine listType ---
          let type: ListTypeProps = null;
          if (elementDOM && elementDOM.tagName) {
            if (elementDOM.tagName.toLowerCase() === "ul") {
              type = "bullet";
            } else if (elementDOM.tagName.toLowerCase() === "ol") {
              type = "number";
            }
          } else {
            // fallback to Lexical node type if DOM is not available
            type = parentList
              ? parentList.getListType()
              : element.getListType();
          }
          setListType(type);
          if (type === "number" && elementDOM) {
            const styleType = elementDOM.style.listStyleType;
            switch (styleType) {
              case "upper-roman":
                setNumberType(numberTypeList["capitalRoman"]);
                break;
              case "lower-roman":
                setNumberType(numberTypeList["smallRoman"]);
                break;
              case "upper-alpha":
                setNumberType(numberTypeList["capitalAlpha"]);
                break;
              case "lower-alpha":
                setNumberType(numberTypeList["smallAlpha"]);
                break;
              case "lower-greek":
                setNumberType(numberTypeList["lowerGreek"]);
                break;
              default:
                setNumberType(numberTypeList["number"]);
            }
          } else if (type === "bullet" && elementDOM) {
            let styleType = elementDOM.style.listStyleType;

            // Only normalize if styleType is truly invalid (not set or not a valid bullet style)
            if (!styleType || !["disc", "circle", "square", "none"].includes(styleType)) {
              // Try to infer from bulletType state, else default to disc
              switch (bulletType) {
                case "circle":
                  styleType = "circle";
                  elementDOM.style.listStyleType = "circle";
                  break;
                case "square":
                  styleType = "square";
                  elementDOM.style.listStyleType = "square";
                  break;
                default:
                  styleType = "disc";
                  elementDOM.style.listStyleType = "disc";
              }
            }

            // Now update state based on the (possibly fixed) styleType
            switch (styleType) {
              case "circle":
                setBulletType("circle");
                break;
              case "square":
                setBulletType("square");
                break;
              case "none":
                setBulletType("bullet");
                break;
              default:
                setBulletType("bullet");
            }
          }

        } else {
          const type = $isHeadingNode(element)
            ? element.getTag()
            : element.getType();
          if (type in blockTypeToBlockName) {
            setBlockType(type as keyof typeof blockTypeToBlockName);
          }
          setListType(null);
        }
      }

      // Update text format
      setIsBold(selection.hasFormat("bold"));
      setIsItalic(selection.hasFormat("italic"));
      setIsUnderline(selection.hasFormat("underline"));

      setFontColor(
        $getSelectionStyleValueForProperty(selection, "color", "#000")
      );
      setBgColor(
        $getSelectionStyleValueForProperty(
          selection,
          "background-color",
          "#fff"
        )
      );
      const editorContent = editor._rootElement?.innerHTML || "";
      const editorIsEmpty = isEditorEmpty(editorContent);

      if (changeStateEditorBody) {
        if (messageBody && editorIsEmpty) {
          changeStateEditorBody("");
        } else if (!editorIsEmpty) {
          changeStateEditorBody(editorContent);
        }
      }
    }
  }, [activeEditor, editor, bulletType]);

  const isEditorEmpty = (htmlContent: string): boolean => {
    if (!htmlContent) return true;
    const trimmedContent = htmlContent.trim();
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = trimmedContent;
    const isMeaningfulContent =
      tempDiv.textContent?.trim() ||
      tempDiv.querySelector("img, video, audio, iframe");
    return !isMeaningfulContent;
  };

  const handleKeydown = (event: Event): void => {
    const keyboardEvent = event as KeyboardEvent;
    if (
      keyboardEvent.key === "a" &&
      (keyboardEvent.ctrlKey || keyboardEvent.metaKey)
    ) {
      editor.update(() => {
        const root = $getRoot();
        root.clear();
      });

      if (changeStateEditorBody) {
        changeStateEditorBody("");
      }
    }
  };

  editor.registerRootListener(() => {
    const editorElem = document.querySelector(".editor-container");
    if (editorElem) {
      editorElem.addEventListener("keydown", handleKeydown as EventListener);
    }
  });

  const applyStyleText = useCallback(
    (styles: Record<string, string>, skipHistoryStack?: boolean) => {
      activeEditor.update(
        () => {
          const selection = $getSelection();
          if (selection !== null) {
            $patchStyleText(selection, styles);
          }
        },
        skipHistoryStack ? { tag: "historic" } : {}
      );
    },
    [activeEditor]
  );

  useEffect(() => {
    if (!messageBody) return;
    // Parse the original HTML and map list-style-types to their index
    const parser = new DOMParser();
    const doc = parser.parseFromString(messageBody, "text/html");
    const ols = Array.from(doc.querySelectorAll("ol"));
    const styleTypes = ols.map(ol => ol.style.listStyleType || ol.getAttribute("type") || "");

    const uls = Array.from(doc.querySelectorAll("ul"));
    const ulStyleTypes = uls.map(ul => ul.style.listStyleType || ul.getAttribute("type") || "");
    // Now, set the style on the editor's DOM
    setTimeout(() => {
      const editorRoot = editor._rootElement;
      if (!editorRoot) return;
      const editorOls = editorRoot.querySelectorAll("ol");
      editorOls.forEach((ol, idx) => {
        const styleType = styleTypes[idx];
        if (styleType) {
          // If style attribute is present, use it
          ol.style.listStyleType = styleType;
        } else {
          // If type attribute is present, map it
          const typeAttr = ol.getAttribute("type");
          console.log("Type Attribute:", typeAttr);
          if (typeAttr === "I") ol.style.listStyleType = "upper-roman";
          else if (typeAttr === "i") ol.style.listStyleType = "lower-roman";
          else if (typeAttr === "A") ol.style.listStyleType = "upper-alpha";
          else if (typeAttr === "a") ol.style.listStyleType = "lower-alpha";
          else if (typeAttr === "1") ol.style.listStyleType = "decimal";
          else {
            ol.style.listStyleType = "lower-greek";
          }
        }
      });

      const editorUls = editorRoot.querySelectorAll("ul");
      editorUls.forEach((ul, idx) => {
        // Prefer style attribute, then type attribute, then default
        let styleType = ulStyleTypes[idx];
        console.log("UL style Attribute 53:", styleType);

        if (styleType) {
          ul.style.listStyleType = styleType;
        } else {
          const typeAttr = ul.getAttribute("type");
          if (typeAttr === "circle") ul.style.listStyleType = "circle";
          else if (typeAttr === "square") ul.style.listStyleType = "square";
          else if (typeAttr === "disc") ul.style.listStyleType = "disc";
          else if (typeAttr === "none") ul.style.listStyleType = "none";
          else ul.style.listStyleType = "disc"; // Default to disc if no type is specified
        }
      });

      const editorStyles = editorRoot.querySelectorAll("[style]");
      editorStyles.forEach((el) => {
        const style = el.getAttribute('style') || '';
        const cleanedStyle = style
             .split(';')
             .filter((rule) => !rule.trim().startsWith('font-weight'))
             .join(';')
             .trim();

        // Check for bold font-weight
        const isBold = /font-weight\s*:\s*(bold|[5-9]00)/i.test(style);
        if (cleanedStyle) {
          el.setAttribute('style', cleanedStyle);
        }

        if (isBold) {
          const strong = document.createElement('strong');
          strong.innerHTML = el.innerHTML;
          el.replaceWith(strong);
        } 
      });
    }, 0);
  }, [editor, messageBody]);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      (_payload, newEditor) => {
        setActiveEditor(newEditor);
        $updateToolbar();
        return false;
      },
      COMMAND_PRIORITY_CRITICAL
    );
  }, [editor, $updateToolbar]);

  useEffect(() => {
    activeEditor.getEditorState().read(() => {
      $updateToolbar();
    });
  }, [activeEditor, $updateToolbar]);
  
  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, _newEditor) => {
          $updateToolbar();
          return false;
        },
        LowPriority
      ),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        (payload) => {
          setCanUndo(payload);
          return false;
        },
        LowPriority
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        (payload) => {
          setCanRedo(payload);
          return false;
        },
        LowPriority
      ),
      editor.registerCommand(
        INSERT_UNORDERED_LIST_COMMAND,
        () => {
          insertList(editor, "bullet");
          return true;
        },
        LowPriority
      )
    );
  }, [editor, $updateToolbar]);

  const onFontColorSelect = useCallback(
    (value: string, skipHistoryStack: boolean) => {
      applyStyleText({ color: value }, skipHistoryStack);
    },
    [applyStyleText]
  );

  const onBgColorSelect = useCallback(
    (value: string, skipHistoryStack: boolean) => {
      applyStyleText({ "background-color": value }, skipHistoryStack);
    },
    [applyStyleText]
  );

  const componentsMap = {
    help: (props: any) => <Help />,
    edit: (props: any) => (
      <Edit
        {...props}
        editor={activeEditor}
        canRedo={canRedo}
        canUndo={canUndo}
        readOnly={readOnly}
      />
    ),
    format: (props: any) => (
      <Format
        {...props}
        editor={activeEditor}
        blockType={blockType}
        alignment={alignment}
        isBold={isBold}
        isItalic={isItalic}
        isUnderline={isUnderline}
        readOnly={readOnly}
      />
    ),
    undo: (props: any) => (
      <Undo
        {...props}
        editor={activeEditor}
        canUndo={canUndo}
        readOnly={readOnly}
      />
    ),
    redo: (props: any) => (
      <Redo
        {...props}
        editor={activeEditor}
        canRedo={canRedo}
        readOnly={readOnly}
      />
    ),
    bulletList: (props: any) => (
      <BulletList
        {...props}
        editor={activeEditor}
        bulletType={bulletType}
        setBulletType={setBulletType}
        listType={listType}
        setListType={setListType}
      />
    ),
    numberList: (props: any) => (
      <NumberList
        {...props}
        editor={activeEditor}
        numberType={numberType}
        setNumberType={setNumberType}
        listType={listType}
        setListType={setListType}
      />
    ),
    bold: (props: any) => (
      <Bold
        {...props}
        editor={activeEditor}
        active={isBold}
        readOnly={readOnly}
      />
    ),
    italic: (props: any) => (
      <Italic
        {...props}
        editor={activeEditor}
        active={isItalic}
        readOnly={readOnly}
      />
    ),
    underline: (props: any) => (
      <Underline
        {...props}
        editor={activeEditor}
        active={isUnderline}
        readOnly={readOnly}
      />
    ),
    alignments: (props: any) => (
      <Alignments
        {...props}
        editor={activeEditor}
        alignment={alignment}
        readOnly={readOnly}
      />
    ),
    adjust: (props: any) => (
      <Adjust {...props} editor={activeEditor} isRTL={isRTL} />
    ),
    colorPicker: (props: any) => (
      <DropdownColorPicker
        buttonClassName="lexical-toolbarItem"
        buttonAriaLabel="Formatting text color"
        buttonIconClassName={`lexical-icon iconFontColor`}
        color={fontColor}
        iconName="font"
        onChange={onFontColorSelect}
        title={toolTipConstants.fontColorPicker}
        {...props}
        disabled={readOnly}
      />
    ),
    bgColorPicker: (props: any) => (
      <DropdownColorPicker
        buttonClassName={`lexical-toolbarItem lexical-colorPicker`}
        buttonAriaLabel="Formatting background color"
        buttonIconClassName={`icon iconBgColor`}
        color={bgColor}
        iconName="bgColor"
        onChange={onBgColorSelect}
        title={toolTipConstants.bgColorPicker}
        disabled={readOnly}
      />
    ),
    clearFormat: () => <ClearFormat editor={activeEditor} />,
    linkImage: () => <LinkImage editor={activeEditor} />
  };

  const hasEditOrFormat =
    options.includes("edit") || options.includes("format");

  const renderOptions = (filterFn: (option: string) => boolean) => {
    const optionsWithSeparator = ["redo", "underline", "alignments"];

    return options.filter(filterFn).map((option, itemIndex) => (
      <React.Fragment key={itemIndex}>
        {componentsMap[option] && componentsMap[option]({})}
        {optionsWithSeparator.includes(option) && (
          <hr className="lexical-divider" />
        )}
      </React.Fragment>
    ));
  };

  return (
    <>
      <div ref={toolbarRef}>
        {hasEditOrFormat ? (
          <>
            <div className="lexical-toolbar">
              {renderOptions(
                (option) => option === "edit" || option === "format"
              )}
            </div>
            <div className="lexical-toolbar">
              {renderOptions(
                (option) => option !== "edit" && option !== "format"
              )}
            </div>
          </>
        ) : (
          <div className="lexical-toolbar">{renderOptions(() => true)}</div>
        )}
      </div>
      {/* {showSnackbar && (
        <Toast
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            zIndex: 1050,
            minWidth: "300px",
          }}
          onClose={() => setShowSnackbar(false)}
          show={showSnackbar}
          autohide
          delay={3000}
          transition={Fade}
        >
          <Toast.Header closeButton>
            <span>You have reached the maximum character limit!</span>
          </Toast.Header>
        </Toast>
      )} */}
    </>
  );
}
